import {Injectable, OnDestroy} from '@angular/core';
import {BehaviorSubject, Observable, of as observableOf, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {NbLayoutDirection, NbLayoutDirectionService} from '@nebular/theme';

/**
 * Manages layout and sidebar UI state for the application.
 *
 * Exposes observable streams for layout and sidebar selections and
 * adapts iconography based on the current layout direction (LTR/RTL).
 * Subscribes to `NbLayoutDirectionService` to keep icons consistent
 * when the direction changes.
 */
@Injectable()
export class StateService implements OnDestroy {

  /** Signals subscription teardown to avoid memory leaks. */
  protected destroy$ = new Subject<boolean>();

  /**
   * Available application layout options. The first item is the default.
   */
  protected layouts: any = [
    {
      name: 'One Column',
      icon: 'nb-layout-default',
      id: 'one-column',
      selected: true,
    },
    {
      name: 'Two Column',
      icon: 'nb-layout-two-column',
      id: 'two-column',
    },
    {
      name: 'Center Column',
      icon: 'nb-layout-centre',
      id: 'center-column',
    },
  ];

  /**
   * Available sidebar placement options. The first item is the default.
   * Icons automatically flip for RTL languages.
   */
  protected sidebars: any = [
    {
      name: 'Sidebar at layout start',
      icon: 'nb-layout-sidebar-left',
      id: 'start',
      selected: true,
    },
    {
      name: 'Sidebar at layout end',
      icon: 'nb-layout-sidebar-right',
      id: 'end',
    },
  ];

  /** Current layout selection stream. */
  protected layoutState$ = new BehaviorSubject(this.layouts[0]);
  /** Current sidebar selection stream. */
  protected sidebarState$ = new BehaviorSubject(this.sidebars[0]);

  /**
   * Creates the service and wires direction change handling to update
   * sidebar icons for LTR/RTL.
   */
  constructor(directionService: NbLayoutDirectionService) {
    directionService.onDirectionChange()
      .pipe(takeUntil(this.destroy$))
      .subscribe((direction) => this.updateSidebarIcons(direction));

    this.updateSidebarIcons(directionService.getDirection());
  }

  /**
   * Lifecycle hook to complete internal subjects and clean up subscriptions.
   */
  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  /**
   * Sets the current layout state.
   * @param state The selected layout descriptor object.
   */
  setLayoutState(state: any): any {
    this.layoutState$.next(state);
  }

  /**
   * Gets the list of available layout options.
   * @returns Observable sequence of layout descriptors.
   */
  getLayoutStates(): Observable<any[]> {
    return observableOf(this.layouts);
  }

  /**
   * Observable stream of the current layout selection.
   */
  onLayoutState(): Observable<any> {
    return this.layoutState$.asObservable();
  }

  /**
   * Sets the current sidebar state.
   * @param state The selected sidebar descriptor object.
   */
  setSidebarState(state: any): any {
    this.sidebarState$.next(state);
  }

  /**
   * Gets the list of available sidebar options.
   * @returns Observable sequence of sidebar descriptors.
   */
  getSidebarStates(): Observable<any[]> {
    return observableOf(this.sidebars);
  }

  /**
   * Observable stream of the current sidebar selection.
   */
  onSidebarState(): Observable<any> {
    return this.sidebarState$.asObservable();
  }

  /**
   * Updates sidebar icon classes based on layout direction.
   * @param direction Current layout direction (LTR/RTL).
   */
  private updateSidebarIcons(direction: NbLayoutDirection) {
    const [startSidebar, endSidebar] = this.sidebars;
    const isLtr = direction === NbLayoutDirection.LTR;
    const startIconClass = isLtr ? 'nb-layout-sidebar-left' : 'nb-layout-sidebar-right';
    const endIconClass = isLtr ? 'nb-layout-sidebar-right' : 'nb-layout-sidebar-left';
    startSidebar.icon = startIconClass;
    endSidebar.icon = endIconClass;
  }
}
